import React, { Suspense } from "react";
import { useHistory } from "react-router-dom";
import { Column, Container, Row } from "../components";
import { PulseLoader } from "../components/pulse_loader";
import { buildPathWithParams, useMediaQuery } from "../hooks";
import { useI18n } from "../lib/i18n/i18n_provider";
import {
  CatalogOfferingCardV2,
  CatalogOfferingTargetUrlCardV2,
} from "../offering_v2/components/more_to_explore";
import { Grid } from "./grid";
import { Heading } from "./heading";
import { Spacer } from "./spacer";
import { Text } from "./text";
import { BlockCategoryLayoutProps } from "./block_category_layout";
import { Button } from "react-bootstrap";

const dataTestId = "block-template-block-category";
const offeringItemDataTestId = "block-template-catalog-offering-item";

export const CoverCategoryLayout = (props: BlockCategoryLayoutProps) => {
  const { catalogCategory, partnerCatalog } = props;
  const { slug: partnerCatalogSlug } = partnerCatalog;
  const { title, description, cover, slug, ctaLabel } = catalogCategory;
  const history = useHistory();
  const mq = useMediaQuery();

  const { fbt } = useI18n();

  const firstOffering = catalogCategory.catalogOfferings?.[0];
  const exploreLink = slug
    ? `/offerings/category/${slug}`
    : firstOffering?.slug
    ? `/offerings/${firstOffering?.slug}`
    : undefined;

  const onButtonClick = (path: string) => {
    const url = buildPathWithParams(path, {
      reset: true,
      keepOnReset: ["promoCode", "fromCatalog"],
      fromCatalog: partnerCatalogSlug,
    });
    history.push(url);
  };

  return (
    <div data-testid={dataTestId}>
      <Row rtl={mq.device === "mobile"} wrap={mq.device === "mobile"}>
        <div
          className="d-flex"
          style={{
            width: "100%",
            maxWidth: mq.device === "mobile" ? "100%" : "380px",
          }}
        >
          <Column
            flex={1}
            expanded={mq.device === "mobile"}
            justifyContent="center"
          >
            <Container>
              <Heading size="md">{title}</Heading>
              <Spacer size={16} />
              <div
                dangerouslySetInnerHTML={{
                  __html: description,
                }}
              />
              <Spacer size={16} />
              {!!exploreLink && (
                <div>
                  <Button onClick={() => onButtonClick(exploreLink)}>
                    {ctaLabel || fbt("Explore")}
                  </Button>
                </div>
              )}
            </Container>
          </Column>
        </div>

        <Spacer
          size={24}
          direction={mq.device === "mobile" ? "vertical" : "horizontal"}
        />
        <Column
          expanded={mq.device === "mobile"}
          flex={mq.device === "mobile" ? undefined : 1}
        >
          <div
            {...(exploreLink && {
              role: "button",
              onClick: () => onButtonClick(exploreLink),
            })}
            className={mq.device === "mobile" ? "mb-4" : ""}
          >
            <img
              alt={title}
              style={{
                objectFit: "cover",
                width: "100%",
                maxWidth: "900px",
                height: mq.device === "mobile" ? 160 : 320,
              }}
              src={cover}
            />
          </div>
        </Column>
      </Row>
    </div>
  );
};
export const TitleCategoryLayout = (props: BlockCategoryLayoutProps) => {
  const { catalogCategory, ltr } = props;
  const mq = useMediaQuery();
  const {
    title,
    description,
    slug: catalogSlug,
    catalogOfferings,
  } = catalogCategory;

  return (
    <div data-testid={dataTestId}>
      <Row wrap={mq.device === "mobile"} rtl={!ltr && mq.device !== "mobile"}>
        <div style={{ width: "100%", maxWidth: "380px" }}>
          <Column justifyContent="center">
            <Container flex={1}>
              <Heading size="md">{title}</Heading>
              <Spacer size={16} />
              <Text>
                <div
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                />
              </Text>
            </Container>
          </Column>
        </div>
        <Spacer
          size={24}
          direction={mq.device === "mobile" ? "vertical" : "horizontal"}
        />
        <Column flex={1}>
          <Grid itemsPerRow={{ md: 2, default: 1 }} spacing={32}>
            {catalogOfferings?.map((o, index) => {
              const {
                targetUrl,
                title,
                image,
                description,
                slug,
                comingSoon,
                membership,
                ctaLabel,
              } = o;

              return (
                <div data-testid={offeringItemDataTestId} className="h-100">
                  <Container key={title} paddingBottom={32} className="h-100">
                    <Suspense
                      fallback={
                        <Container minHeight={240} center>
                          <PulseLoader />
                        </Container>
                      }
                    >
                      {!targetUrl ? (
                        <CatalogOfferingCardV2
                          key={index}
                          image={image}
                          title={title}
                          description={description}
                          offeringSlug={slug}
                          catalogSlug={catalogSlug}
                          comingSoon={comingSoon}
                          membership={membership}
                          ctaLabel={ctaLabel}
                        />
                      ) : (
                        <CatalogOfferingTargetUrlCardV2
                          key={index}
                          image={image}
                          title={title}
                          description={description}
                          comingSoon={comingSoon}
                          targetUrl={targetUrl}
                          membership={membership}
                          ctaLabel={ctaLabel}
                        />
                      )}
                    </Suspense>
                  </Container>
                </div>
              );
            })}
          </Grid>
        </Column>
      </Row>
    </div>
  );
};
export const DefaultCategoryLayout = (props: BlockCategoryLayoutProps) => {
  const mq = useMediaQuery();
  const { partnerCatalog, catalogCategory } = props;
  const isDesktop = mq.device === "desktop";
  const { slug: catalogSlug } = partnerCatalog;
  const { title, description, catalogOfferings } = catalogCategory;

  return (
    <div
      className={isDesktop ? "row" : ""}
      style={isDesktop ? { justifyContent: "center" } : undefined}
      data-testid={dataTestId}
    >
      <Container>
        <div className="mb-3">
          <Heading size="md">{title}</Heading>
          <Spacer size={24} />
          <div
            className="default-catalog-category-block-description"
            dangerouslySetInnerHTML={{
              __html: description,
            }}
          />
        </div>
        {catalogOfferings && (
          <Grid
            itemsPerRow={{
              md: catalogOfferings.length > 2 ? 3 : 2,
              default: 1,
            }}
            spacing={32}
          >
            {catalogOfferings.map((o, index) => {
              const {
                targetUrl,
                title,
                image,
                description,
                slug,
                comingSoon,
                membership,
                ctaLabel,
              } = o;

              return (
                <div
                  data-testid={offeringItemDataTestId}
                  key={o.id}
                  className="h-100"
                >
                  <Container key={title} paddingBottom={32} className="h-100">
                    <Suspense
                      fallback={
                        <Container minHeight={240} center>
                          <PulseLoader />
                        </Container>
                      }
                    >
                      {!targetUrl ? (
                        <CatalogOfferingCardV2
                          key={index}
                          image={image}
                          title={title}
                          description={description}
                          offeringSlug={slug}
                          catalogSlug={catalogSlug}
                          comingSoon={comingSoon}
                          membership={membership}
                          ctaLabel={ctaLabel}
                        />
                      ) : (
                        <CatalogOfferingTargetUrlCardV2
                          key={index}
                          image={image}
                          title={title}
                          description={description}
                          comingSoon={comingSoon}
                          targetUrl={targetUrl}
                          membership={membership}
                          ctaLabel={ctaLabel}
                        />
                      )}
                    </Suspense>
                  </Container>
                </div>
              );
            })}
          </Grid>
        )}
      </Container>
    </div>
  );
};
